// =============================================================================
// pageCache.js, Safari tab-suspension resilient page-state cache
// =============================================================================
//
// Why this exists:
//   Safari aggressively unloads background tabs from memory after ~1–2 minutes.
//   When the user returns, React re-mounts the route, every `useState` resets
//   to its initial value, and the page renders an empty/loading state until
//   Supabase replies (which itself can hang because the SDK is mid-token-refresh).
//   The net effect for the user: "my data disappeared, I had to refresh."
//
// Strategy (calm but reliable, à la Notion / Google Calendar):
//   1. Mirror the last-known good fetch result into both in-memory `MEM` and
//      `sessionStorage`. `sessionStorage` survives the Safari heap purge.
//   2. Pages read the cache *synchronously* during the `useState` initialiser
//      so the very first paint after remount already shows the previous data  
//      no flicker, no skeleton, no "No data yet" empty state.
//   3. The page still fires its fetch in the background (silently). If it
//      succeeds, the new data overwrites the cache. If it fails or times out,
//      the previously cached data stays on screen.
//
// Privacy & correctness:
//   * Cache keys are prefixed with the user's id so two users sharing a device
//     never see each other's data.
//   * `sessionStorage` (not `localStorage`) is used so the cache is cleared
//     automatically when the browser session ends, no long-lived footprint.
//   * Entries have a built-in TTL (default 24h). Anything older is discarded
//     on read so we never hydrate state from very stale snapshots.
//
// This module is intentionally framework-agnostic and side-effect-free at
// import time so it can be tree-shaken if unused.

const SS_PREFIX = '2120_pd_';
const MEM = new Map(); // key -> { data, ts }
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24h

function _ssAvailable() {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return false;
    // Safari Private Browsing throws on setItem, sniff for that early.
    window.sessionStorage.setItem('__2120_probe__', '1');
    window.sessionStorage.removeItem('__2120_probe__');
    return true;
  } catch (_) {
    return false;
  }
}
const SS_OK = _ssAvailable();

function _scopedKey(userId, key) {
  return userId ? `${userId}:${key}` : `anon:${key}`;
}

/**
 * Read a previously cached value for this user+key combination.
 * Returns `null` if not found, expired, or unreadable.
 */
export function readCache(userId, key, ttlMs = DEFAULT_TTL_MS) {
  const k = _scopedKey(userId, key);
  // Prefer in-memory (faster + always available).
  const inMem = MEM.get(k);
  if (inMem && Date.now() - inMem.ts <= ttlMs) {
    return inMem.data;
  }
  if (!SS_OK) return null;
  try {
    const raw = window.sessionStorage.getItem(SS_PREFIX + k);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > ttlMs) {
      // Stale, clean it up so it doesn't accumulate.
      window.sessionStorage.removeItem(SS_PREFIX + k);
      return null;
    }
    MEM.set(k, parsed);
    return parsed.data;
  } catch (_) {
    return null;
  }
}

/**
 * Write a value to the cache. Fire-and-forget; never throws to the caller.
 */
export function writeCache(userId, key, data) {
  const k = _scopedKey(userId, key);
  const entry = { data, ts: Date.now() };
  MEM.set(k, entry);
  if (!SS_OK) return;
  try {
    window.sessionStorage.setItem(SS_PREFIX + k, JSON.stringify(entry));
  } catch (_) {
    // Quota exceeded or serialization failure, silently drop. The in-memory
    // copy still survives the current page session, just not a hard remount.
  }
}

/**
 * Delete a cached entry (e.g., after a sign-out or destructive mutation).
 */
export function clearCache(userId, key) {
  const k = _scopedKey(userId, key);
  MEM.delete(k);
  if (!SS_OK) return;
  try { window.sessionStorage.removeItem(SS_PREFIX + k); } catch (_) {}
}

/**
 * Race a promise against a deadline. On timeout, the rejection contains a
 * `name === 'TimeoutError'` so callers can branch on it cleanly. Use this on
 * every Supabase query, the underlying SDK has a known hang condition during
 * mid-flight token refreshes on tab refocus.
 */
export function withTimeout(promise, ms = 12_000, label = 'op') {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      const err = new Error(`Timed out after ${ms}ms: ${label}`);
      err.name = 'TimeoutError';
      reject(err);
    }, ms);
    Promise.resolve(promise).then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

/**
 * Convenience helper: run `fn`, retry once on timeout/network errors, never
 * throw. Returns `{ ok, data, error }`. Useful for fire-and-forget refetches
 * where we'd rather keep the prior state than surface a failure.
 */
export async function safeRun(fn, { timeoutMs = 12_000, label = 'op' } = {}) {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const data = await withTimeout(fn(), timeoutMs, label);
      return { ok: true, data, error: null };
    } catch (e) {
      if (attempt === 1) return { ok: false, data: null, error: e };
      // Retry once after a short delay, only if the error looks transient.
      if (!_looksTransient(e)) return { ok: false, data: null, error: e };
      await new Promise((r) => setTimeout(r, 300));
    }
  }
  return { ok: false, data: null, error: new Error('exhausted retries') };
}

function _looksTransient(err) {
  if (!err) return false;
  if (err.name === 'TimeoutError') return true;
  const msg = String(err.message || err).toLowerCase();
  return msg.includes('network') || msg.includes('fetch') || msg.includes('abort');
}
